/**
 * LakePalaceDestinationExperience — Udaipur-style destination wedding journey.
 *
 * Five scroll-scrubbed plot-plane beats:
 *   1 Misty lake approach — couple names + reflection shimmer
 *   2 Marble jetty / boat arrival
 *   3 Lake-palace courtyard hero — date & venue
 *   4 Function arcade over water — Mehndi / Sangeet / Ceremony / Reception
 *   5 RSVP terrace with map + travel niche
 *
 * Craft rules: mobile WhatsApp-first hierarchy (names → event → date/venue →
 * RSVP), honest placeholders only, high-contrast scrims over every plate,
 * bottom safe zone left clear for the InviteCloud watermark and the partner
 * attribution footer (both rendered by the host page, never by this template),
 * and no fixed overlays that could crop faces or block tap targets.
 */

import React from 'react';
import { useReducedMotion } from 'motion/react';
import { PlotPlane, type PlotBeat } from './PlotPlane';
import { resolveContent, type LakePalaceContent } from './content';
import type { InvitationData } from '@/src/modules/templates/types';
import './lake-palace.css';

const ASSET = (name: string) => `/assets/templates/lake-palace-destination/${name}`;

/** Mobile crops the wide plates around centre — bias right where subjects sit. */
const COVER_MOBILE_CENTER = 'object-[50%_48%] md:object-center';
const COVER_MOBILE_RIGHT = 'object-[71%_52%] md:object-center';

function Kicker({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <p className={`lp-display text-[0.72rem] uppercase tracking-[0.4em] text-[#e7c98a] sm:text-[0.85rem] sm:tracking-[0.46em] ${className}`}>
      {children}
    </p>
  );
}

function GoldRule() {
  return (
    <div aria-hidden="true" className="mx-auto my-4 flex w-24 items-center justify-center gap-2 sm:my-5 sm:w-32">
      <span className="h-px flex-1 bg-gradient-to-r from-transparent to-[#d9b26a]/80" />
      <span className="h-1.5 w-1.5 rotate-45 border border-[#e7c98a]/90" />
      <span className="h-px flex-1 bg-gradient-to-l from-transparent to-[#d9b26a]/80" />
    </div>
  );
}

const GOLD_PILL =
  'inline-flex min-h-[44px] items-center justify-center rounded-full border border-[#e7c98a]/70 bg-[#e7c98a]/12 px-6 ' +
  'text-sm tracking-[0.12em] text-[#f7e0ae] transition-colors duration-200 hover:bg-[#e7c98a]/22 ' +
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#e7c98a]';

function NamesStack({ content, className = '' }: { content: LakePalaceContent; className?: string }) {
  const { lines, isCouple } = content.names;
  return (
    <div className={`lp-display text-white ${className}`}>
      {isCouple ? (
        <>
          <span className="block leading-[1.02]">{lines[0]}</span>
          <span className="my-1 block text-[0.42em] italic leading-none text-[#e7c98a] sm:my-2">&</span>
          <span className="block leading-[1.02]">{lines[1]}</span>
        </>
      ) : (
        <span className="block leading-[1.06]">{lines[0]}</span>
      )}
    </div>
  );
}

/* ── Beat 1 — misty lake approach ───────────────────────────────────── */
function ApproachBeat({ content, showCue }: { content: LakePalaceContent; showCue: boolean }) {
  return (
    <div className="flex w-full max-w-[min(94vw,44rem)] flex-col items-center px-5 text-center">
      <Kicker>Together with their families</Kicker>
      <div className="mt-6 w-full sm:mt-8">
        <h1
          className="sr-only"
        >{`${content.names.joined} — destination wedding invitation`}</h1>
        <div aria-hidden="true" className="text-[clamp(2.35rem,11.5vw,5.2rem)]">
          <NamesStack content={content} />
          <div className="lp-reflect select-none">
            <NamesStack content={content} className="lp-reflect-inner" />
            <span className="lp-reflect-sweep" />
          </div>
        </div>
      </div>
      <p className="mt-6 max-w-sm text-sm leading-relaxed text-white/85 sm:mt-8 sm:text-base">
        invite you to celebrate their wedding, where the palace meets the lake.
      </p>
      {showCue && (
        <div className="mt-10 flex flex-col items-center gap-2 text-white/70 sm:mt-14">
          <span className="text-[0.62rem] uppercase tracking-[0.4em]">Scroll to set sail</span>
          <svg aria-hidden="true" viewBox="0 0 24 24" className="lp-bob h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.6">
            <path d="M12 4v14m0 0 6-6m-6 6-6-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      )}
    </div>
  );
}

/* ── Beat 2 — marble jetty / boat arrival ───────────────────────────── */
function ArrivalBeat({ content }: { content: LakePalaceContent }) {
  return (
    <div className="flex w-full max-w-[min(94vw,38rem)] flex-col items-center px-5 text-center">
      <div className="lp-card w-full rounded-2xl px-6 py-7 sm:px-9 sm:py-9">
        <Kicker>The Arrival</Kicker>
        <h2 className="lp-display mt-3 text-[clamp(1.75rem,7.5vw,2.9rem)] leading-tight text-white">
          Step onto the marble jetty
        </h2>
        <GoldRule />
        <p className="mx-auto max-w-md text-sm leading-relaxed text-white/85 sm:text-base">
          {content.message || 'Our families welcome you to the water’s edge for a celebration made of mist, marble and music.'}
        </p>
        {content.hostLine && (
          <p className="mt-5 text-[0.7rem] uppercase tracking-[0.3em] text-[#e7c98a]">Hosted by {content.hostLine}</p>
        )}
      </div>
    </div>
  );
}

/* ── Beat 3 — courtyard hero: date & venue ──────────────────────────── */
function CourtyardBeat({ content }: { content: LakePalaceContent }) {
  const { date } = content;
  return (
    <div className="flex w-full max-w-[min(94vw,36rem)] justify-center px-5">
      <div className="lp-card w-full rounded-2xl px-6 py-7 text-center sm:px-9 sm:py-9">
        <Kicker>The Courtyard</Kicker>
        {date.hasDate ? (
          <div className="mt-4">
            <p className="text-[0.7rem] uppercase tracking-[0.42em] text-white/75">{date.weekday}</p>
            <p className="lp-display mt-1 text-[clamp(3.4rem,17vw,6rem)] leading-none text-white">{date.day}</p>
            <p className="mt-1 text-sm uppercase tracking-[0.36em] text-[#e7c98a] sm:text-base">{date.monthYear}</p>
          </div>
        ) : (
          <p className="lp-display mt-4 text-2xl text-white">Date to be announced</p>
        )}
        <GoldRule />
        <p className="lp-display text-[clamp(1.15rem,5vw,1.7rem)] leading-snug text-white">{content.venue}</p>
        {date.time && <p className="mt-1.5 text-sm tracking-wide text-white/70">{date.time}</p>}
        {content.mapsUrl && (
          <a
            href={content.mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`${GOLD_PILL} mt-6`}
          >
            Open in Maps
          </a>
        )}
      </div>
    </div>
  );
}

/* ── Beat 4 — function arcade over water ────────────────────────────── */
function ArcadeBeat({ content }: { content: LakePalaceContent }) {
  return (
    <div className="flex w-full max-w-[min(94vw,52rem)] flex-col items-center px-5 text-center">
      <Kicker>The Arcade over the Water</Kicker>
      <h2 className="lp-display mt-3 text-[clamp(1.7rem,7vw,2.8rem)] leading-tight text-white">
        Four evenings of celebration
      </h2>
      <div className="mt-6 grid w-full grid-cols-2 gap-2.5 sm:mt-8 sm:gap-4">
        {content.functions.map((fn) => (
          <div
            key={fn.key}
            className="lp-card flex flex-col rounded-xl px-3 py-3 text-left sm:px-5 sm:py-4"
          >
            <h3 className="lp-display text-lg leading-tight text-white sm:text-2xl">{fn.title}</h3>
            {fn.confirmed ? (
              <>
                <p className="mt-1 text-[0.68rem] uppercase tracking-[0.18em] text-[#e7c98a] sm:text-[0.72rem]">
                  {fn.dateLabel}
                </p>
                {fn.timeLabel && (
                  <p className="mt-0.5 text-[0.68rem] tracking-wide text-white/70 sm:text-xs">{fn.timeLabel}</p>
                )}
                {fn.venueLabel && (
                  <p className="mt-1 line-clamp-2 text-[0.66rem] leading-snug text-white/55 sm:text-[0.75rem]">
                    {fn.venueLabel}
                  </p>
                )}
              </>
            ) : (
              <p className="mt-1 text-[0.68rem] italic leading-snug text-white/55 sm:text-xs">
                Schedule to be announced by the host
              </p>
            )}
            {fn.mapsUrl && fn.confirmed && (
              <a
                href={fn.mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex min-h-[28px] items-center text-[0.66rem] tracking-wide text-[#f7e0ae] underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-[#e7c98a] sm:text-[0.72rem]"
              >
                Map ↗
              </a>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Beat 5 — RSVP terrace + travel niche ───────────────────────────── */
function TerraceBeat({ content }: { content: LakePalaceContent }) {
  return (
    <div className="flex w-full max-w-[min(94vw,34rem)] justify-center px-5">
      <div className="lp-card w-full rounded-2xl px-6 py-7 sm:px-9 sm:py-8">
        <div className="text-center">
          <Kicker>The Terrace Awaits</Kicker>
          <h2 className="lp-display mt-3 text-[clamp(1.7rem,7vw,2.7rem)] leading-tight text-white">
            Be there as we begin forever
          </h2>
          <GoldRule />
          {content.rsvpUrl ? (
            <a href={content.rsvpUrl} target="_blank" rel="noopener noreferrer" className={GOLD_PILL}>
              Confirm your presence
            </a>
          ) : (
            <p className="mx-auto max-w-xs text-sm italic leading-relaxed text-white/75">
              RSVP details will be shared by the host.
            </p>
          )}
        </div>

        <div className="mt-6 rounded-xl border border-white/15 bg-white/[0.06] px-4 py-4 text-left sm:px-5">
          <p className="text-[0.62rem] uppercase tracking-[0.34em] text-[#e7c98a]">Getting there</p>
          <p className="mt-2 text-sm leading-snug text-white/90">{content.venue}</p>
          {content.mapsUrl && (
            <a
              href={content.mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex min-h-[32px] items-center text-xs tracking-wide text-[#f7e0ae] underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-[#e7c98a]"
            >
              View on map ↗
            </a>
          )}
          <p className="mt-2 text-[0.72rem] leading-relaxed text-white/60">{content.travelNote}</p>
        </div>

        {content.hostLine && (
          <p className="mt-5 text-center text-[0.66rem] uppercase tracking-[0.28em] text-white/55">
            {content.hostLine}
            {content.date.hasDate ? ` · ${content.date.long}` : ''}
          </p>
        )}
      </div>
    </div>
  );
}

/* ── Experience root ────────────────────────────────────────────────── */
export function LakePalaceDestinationExperience({ data }: { data: InvitationData }) {
  const reduced = useReducedMotion() === true;
  const content = React.useMemo(() => resolveContent(data), [data]);

  const beats: PlotBeat[] = React.useMemo(
    () => [
      {
        id: 'lp-approach',
        label: 'Misty lake approach',
        mist: true,
        shimmer: true,
        layers: [
          { key: 'horizon', src: ASSET('lake-horizon.svg'), depth: 0, className: COVER_MOBILE_RIGHT },
        ],
        content: <ApproachBeat content={content} showCue={!reduced} />,
      },
      {
        id: 'lp-arrival',
        label: 'Marble jetty arrival',
        layers: [
          { key: 'horizon', src: ASSET('lake-horizon.svg'), depth: 0, className: COVER_MOBILE_RIGHT },
          { key: 'jetty', src: ASSET('jetty-boat.svg'), depth: 0.55, anchor: 'bottom' },
          { key: 'rail', src: ASSET('balustrade.svg'), depth: 1, anchor: 'bottom', className: 'opacity-95' },
        ],
        content: <ArrivalBeat content={content} />,
      },
      {
        id: 'lp-courtyard',
        label: 'Palace courtyard with date and venue',
        shimmer: true,
        layers: [
          { key: 'courtyard', src: ASSET('palace-courtyard.svg'), depth: 0, className: COVER_MOBILE_CENTER },
          { key: 'rail', src: ASSET('balustrade.svg'), depth: 1, anchor: 'bottom', className: 'opacity-90' },
        ],
        content: <CourtyardBeat content={content} />,
      },
      {
        id: 'lp-arcade',
        label: 'Function arcade over water',
        layers: [
          { key: 'arcade', src: ASSET('arcade-over-water.svg'), depth: 0, className: COVER_MOBILE_CENTER },
        ],
        content: <ArcadeBeat content={content} />,
      },
      {
        id: 'lp-terrace',
        label: 'RSVP terrace with travel notes',
        layers: [
          { key: 'terrace', src: ASSET('terrace-durbar.svg'), depth: 0, className: COVER_MOBILE_CENTER },
          { key: 'rail', src: ASSET('balustrade.svg'), depth: 1, anchor: 'bottom' },
        ],
        content: <TerraceBeat content={content} />,
      },
    ],
    [content, reduced],
  );

  return (
    <div className="lp-root relative bg-[#08202f] text-white antialiased">
      <PlotPlane beats={beats} />
    </div>
  );
}
